/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: careers.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBriefcase, FaLocationDot } from 'react-icons/fa6';
import { MdOutlineError } from 'react-icons/md';
import { Spinner } from '@nextui-org/react';
import { motion } from 'framer-motion';
import { QueryDocumentSnapshot, QuerySnapshot, collection, getDocs, query } from 'firebase/firestore';

import Statics from '@/constants/statics';
import Section from '@/shared/section';
import ThemedButtons from '@/shared/themed-buttons';
import FirebaseService from '@/services';
import Converters from '@/services/converters';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
enum JobPostState {
  LOADING = 1,
  SUCCESS = 2,
  FAILED = 3
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const Careers: FunctionComponent = () => {
  const { t } = useTranslation('careers');

  const [jobPosts, setJobPosts] = useState<JobPosting[]>([]);

  const [status, setStatus] = useState<JobPostState>(JobPostState.LOADING);

  useEffect(() => {
    getDocs(
      query(
        collection(FirebaseService.StoreDB, 'careers').withConverter<JobPosting>(Converters.JobPost)
      )
    )
      .then((snapshot: QuerySnapshot<JobPosting>) => {
        setJobPosts(
          snapshot.docs.map((documentSnapshot: QueryDocumentSnapshot<JobPosting>) =>
            documentSnapshot.data()
          )
        );
        setTimeout(() => setStatus(JobPostState.SUCCESS), 2000);
      })
      .catch((err) => {
        console.log(err);
        setTimeout(() => setStatus(JobPostState.FAILED), 2000);
      });
  }, []);

  return (
    <Section id="careers" className="bg-white">
      <div className="w-full flex flex-col items-center justify-start gap-10">
        <motion.div
          className="w-full bg-themeBlue-900 flex items-center justify-start gap-3 p-5"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: -100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          <p className="text-lg text-white font-inter font-light">
            {t('send-resumes.title')} -{' '}
            <a
              className="text-white/60 hover:text-white font-aldrich transition-all duration-350"
              href={Statics.EmailItems[1].href}
            >
              {Statics.EmailItems[1].label}
            </a>
          </p>
        </motion.div>
        <div className="w-full flex flex-col items-center justify-start gap-5">
          <div className="w-full min-h-[40px] border-b-[1px] border-b-themePurple-900 flex flex-col items-center justify-center">
            <motion.p
              className="w-full text-lg text-themePurple-900 font-aldrich font-medium uppercase"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: -100,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('open-positions.title')}
            </motion.p>
          </div>
          {status === JobPostState.FAILED ? (
            <div className="w-full bg-red-500 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
              <div className="w-[25px] h-[25px] flex items-center justify-center">
                <MdOutlineError className="text-xl text-white" />
              </div>
              <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
                {t('open-positions.error')}
              </p>
            </div>
          ) : status === JobPostState.SUCCESS && jobPosts.length > 0 ? (
            <ul className="w-full flex flex-col items-center justify-start gap-3">
              {jobPosts.map(({ id, position, location, type }, idx: number) => (
                <motion.li
                  key={id}
                  className="w-full h-full h-fit bg-themePurple-50 flex flex-wrap items-center justify-between gap-5 p-5"
                  initial="initial"
                  whileInView="entry"
                  viewport={{ once: true }}
                  variants={{
                    initial: {
                      x: -50 - idx * 10,
                      opacity: 0
                    },
                    entry: {
                      x: 0,
                      opacity: 1
                    }
                  }}
                  transition={{
                    duration: 0.5
                  }}
                >
                  <div className="h-full flex flex-col items-start justify-start gap-2">
                    <p className="text-lg text-themePurple-900 font-aldrich font-semibold uppercase">
                      {position}
                    </p>
                    <div className="flex items-center justify-start gap-5">
                      <div className="flex items-center justify-start gap-2">
                        <FaLocationDot className="text-themePurple-900" />
                        <p className="text-sm text-themePurple-700 font-inter font-medium">
                          {location}
                        </p>
                      </div>
                      <div className="flex items-center justify-start gap-2">
                        <FaBriefcase className="text-themePurple-900" />
                        <p className="text-sm text-themePurple-700 font-inter font-medium">
                          {type}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="h-full flex flex-col items-center justify-center gap-2">
                    <ThemedButtons.Link
                      className="bg-themePurple-900/80 hover:bg-themePurple-900 !text-white"
                      href={`${Statics.EmailItems[1].href}?subject=${encodeURIComponent(
                        `Job Application - ${position}`
                      )}`}
                    >
                      {t('open-positions.btns.send-resume')}
                    </ThemedButtons.Link>
                  </div>
                </motion.li>
              ))}
            </ul>
          ) : status === JobPostState.LOADING ? (
            <div className="w-full bg-themeBlue-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
              <div className="w-[25px] h-[25px] flex items-center justify-center">
                <Spinner size="sm" color="white" />
              </div>
              <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
                {t('open-positions.loading')}
              </p>
            </div>
          ) : (
            <div className="w-full bg-themePurple-900 relative flex items-center justify-start gap-[8px] rounded-lg p-5">
              <div className="w-[25px] h-[25px] flex items-center justify-center">
                <MdOutlineError className="text-xl text-white" />
              </div>
              <p className="w-[calc(100%-33px)]text-lg text-white font-inter font-light">
                {t('open-positions.empty')}
              </p>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default Careers;
